import React, { useState, useEffect } from 'react'
import { getLocalApiUrl, authFetch } from '../../config/api'
import {
  PlusIcon,
  PencilSquareIcon,
  TrashIcon,
  PlayIcon,
  ClockIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline'

const CRON_PRESETS = [
  { label: 'Daily at 2:00 AM', value: '0 2 * * *' },
  { label: 'Every 6 hours', value: '0 */6 * * *' },
  { label: 'Weekly (Sunday midnight)', value: '0 0 * * 0' },
  { label: 'Monthly (1st at 1:30 AM)', value: '30 1 1 * *' },
  { label: 'Every hour', value: '0 * * * *' },
]

function formatDateTime(d) {
  if (!d) return '—'
  const dt = new Date(d)
  return isNaN(dt.getTime()) ? '—' : dt.toLocaleString()
}

function RuleScheduler() {
  const [schedules, setSchedules] = useState([])
  const [rules, setRules] = useState([])
  const [loading, setLoading] = useState(true)
  const [modalOpen, setModalOpen] = useState(false)
  const [editingId, setEditingId] = useState(null)
  const [form, setForm] = useState({
    name: '',
    description: '',
    cron_expression: '0 2 * * *',
    rule_ids: [],
    enabled: true,
  })
  const [submitting, setSubmitting] = useState(false)
  const [runNowId, setRunNowId] = useState(null)

  const fetchSchedules = async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/rule-schedules'))
      const j = await r.json()
      if (j.success && Array.isArray(j.data)) setSchedules(j.data)
    } catch (e) {
      console.error('Failed to fetch schedules', e)
    }
  }

  const fetchRules = async () => {
    try {
      const r = await authFetch(getLocalApiUrl('/api/rules/db'))
      const j = await r.json()
      if (j.success && Array.isArray(j.data)) setRules(j.data)
    } catch (e) {
      console.error('Failed to fetch rules', e)
    }
  }

  useEffect(() => {
    ;(async () => {
      setLoading(true)
      await Promise.all([fetchSchedules(), fetchRules()])
      setLoading(false)
    })()
  }, [])

  const openCreate = () => {
    setEditingId(null)
    setForm({
      name: '',
      description: '',
      cron_expression: '0 2 * * *',
      rule_ids: [],
      enabled: true,
    })
    setModalOpen(true)
  }

  const openEdit = (s) => {
    setEditingId(s.id)
    setForm({
      name: s.name,
      description: s.description || '',
      cron_expression: s.cron_expression || '0 2 * * *',
      rule_ids: Array.isArray(s.rule_ids) ? [...s.rule_ids] : [],
      enabled: !!s.enabled,
    })
    setModalOpen(true)
  }

  const handleSubmit = async (e) => {
    e.preventDefault()
    setSubmitting(true)
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description.trim() || undefined,
        cron_expression: form.cron_expression,
        rule_ids: form.rule_ids,
        enabled: form.enabled,
      }
      if (editingId) {
        const r = await authFetch(getLocalApiUrl(`/api/rule-schedules/${editingId}`), {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const j = await r.json()
        if (!j.success) throw new Error(j.error || 'Update failed')
      } else {
        const r = await authFetch(getLocalApiUrl('/api/rule-schedules'), {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(payload),
        })
        const j = await r.json()
        if (!j.success) throw new Error(j.error || 'Create failed')
      }
      setModalOpen(false)
      await fetchSchedules()
    } catch (err) {
      alert(err.message || 'Failed to save')
    } finally {
      setSubmitting(false)
    }
  }

  const handleDelete = async (id) => {
    if (!confirm('Delete this schedule?')) return
    try {
      const r = await authFetch(getLocalApiUrl(`/api/rule-schedules/${id}`), { method: 'DELETE' })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Delete failed')
      await fetchSchedules()
    } catch (err) {
      alert(err.message || 'Failed to delete')
    }
  }

  const handleRunNow = async (id) => {
    setRunNowId(id)
    try {
      const r = await authFetch(getLocalApiUrl(`/api/rule-schedules/${id}/run-now`), { method: 'POST' })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Run failed')
      alert(j.message || 'Run requested. The scheduler service will execute when available.')
      await fetchSchedules()
    } catch (err) {
      alert(err.message || 'Failed to run')
    } finally {
      setRunNowId(null)
    }
  }

  const toggleRule = (ruleId) => {
    setForm((f) => ({
      ...f,
      rule_ids: f.rule_ids.includes(ruleId)
        ? f.rule_ids.filter((id) => id !== ruleId)
        : [...f.rule_ids, ruleId],
    }))
  }

  if (loading) {
    return (
      <div className="p-6">
        <div className="text-gray-500">Loading schedules…</div>
      </div>
    )
  }

  return (
    <div className="p-6 max-w-5xl">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-xl font-bold text-gray-900 dark:text-gray-100">Rule Scheduler</h1>
          <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
            Configure batch rule execution at scheduled times. The scheduler service runs rules via cron.
          </p>
        </div>
        <button
          onClick={openCreate}
          className="inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-psg-teal text-white hover:bg-psg-teal/90 font-medium text-sm"
        >
          <PlusIcon className="w-4 h-4" />
          New Schedule
        </button>
      </div>

      <div className="space-y-4">
        {schedules.length === 0 ? (
          <div className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-8 text-center">
            <ClockIcon className="w-12 h-12 mx-auto text-gray-300 dark:text-gray-600 mb-3" />
            <p className="text-gray-500 dark:text-gray-400">No schedules yet</p>
            <p className="text-sm text-gray-400 dark:text-gray-500 mt-1">Create a schedule to run rules at set times</p>
            <button
              onClick={openCreate}
              className="mt-4 inline-flex items-center gap-2 px-4 py-2 rounded-lg bg-psg-teal text-white hover:bg-psg-teal/90 font-medium text-sm"
            >
              <PlusIcon className="w-4 h-4" />
              Create Schedule
            </button>
          </div>
        ) : (
          schedules.map((s) => (
            <div
              key={s.id}
              className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 p-4 flex items-center justify-between gap-4"
            >
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="font-semibold text-gray-900 dark:text-gray-100">{s.name}</h3>
                  {!s.enabled && (
                    <span className="text-[10px] font-medium px-2 py-0.5 rounded bg-gray-200 dark:bg-gray-600 text-gray-600 dark:text-gray-300">
                      Paused
                    </span>
                  )}
                  {s.last_run_status && (
                    <span
                      className={`text-[10px] font-medium px-2 py-0.5 rounded ${
                        s.last_run_status === 'success'
                          ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                          : s.last_run_status === 'failed'
                          ? 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                          : 'bg-amber-100 text-amber-800 dark:bg-amber-900/30 dark:text-amber-300'
                      }`}
                    >
                      {s.last_run_status}
                    </span>
                  )}
                </div>
                {s.description && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">{s.description}</p>
                )}
                <div className="flex flex-wrap gap-4 mt-2 text-xs text-gray-500 dark:text-gray-400">
                  <span>Cron: <code className="bg-gray-100 dark:bg-gray-700 px-1 rounded">{s.cron_expression}</code></span>
                  <span>{Array.isArray(s.rule_ids) ? s.rule_ids.length : 0} rules</span>
                  <span>Last run: {formatDateTime(s.last_run_at)}</span>
                  <span>Next run: {formatDateTime(s.next_run_at)}</span>
                </div>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  onClick={() => handleRunNow(s.id)}
                  disabled={!!runNowId}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                  title="Run now"
                >
                  <PlayIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => openEdit(s)}
                  className="p-2 rounded-lg border border-gray-200 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700 text-gray-600 dark:text-gray-300"
                  title="Edit"
                >
                  <PencilSquareIcon className="w-4 h-4" />
                </button>
                <button
                  onClick={() => handleDelete(s.id)}
                  className="p-2 rounded-lg border border-red-200 dark:border-red-900/50 hover:bg-red-50 dark:hover:bg-red-900/20 text-red-600 dark:text-red-400"
                  title="Delete"
                >
                  <TrashIcon className="w-4 h-4" />
                </button>
              </div>
            </div>
          ))
        )}
      </div>

      {modalOpen && (
        <div className="fixed inset-0 bg-black/30 z-50 flex items-center justify-center p-4">
          <div className="bg-white dark:bg-gray-800 rounded-xl w-full max-w-lg max-h-[90vh] overflow-auto shadow-xl">
            <div className="flex justify-between items-center p-4 border-b border-gray-200 dark:border-gray-700">
              <h2 className="text-lg font-bold text-psg-teal dark:text-psg-teal-300">
                {editingId ? 'Edit Schedule' : 'New Schedule'}
              </h2>
              <button
                onClick={() => setModalOpen(false)}
                className="p-1 hover:bg-gray-100 dark:hover:bg-gray-700 rounded"
              >
                <XMarkIcon className="w-5 h-5 text-gray-400" />
              </button>
            </div>
            <form onSubmit={handleSubmit} className="p-4 space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Name</label>
                <input
                  type="text"
                  value={form.name}
                  onChange={(e) => setForm((f) => ({ ...f, name: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  placeholder="e.g. Daily AML Check"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Description</label>
                <textarea
                  value={form.description}
                  onChange={(e) => setForm((f) => ({ ...f, description: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100"
                  rows={2}
                  placeholder="Optional description"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Schedule</label>
                <div className="flex flex-wrap gap-2 mb-2">
                  {CRON_PRESETS.map((p) => (
                    <button
                      key={p.value}
                      type="button"
                      onClick={() => setForm((f) => ({ ...f, cron_expression: p.value }))}
                      className={`text-xs px-2.5 py-1.5 rounded-lg border ${
                        form.cron_expression === p.value
                          ? 'border-psg-teal bg-psg-teal/10 text-psg-teal'
                          : 'border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700'
                      }`}
                    >
                      {p.label}
                    </button>
                  ))}
                </div>
                <input
                  type="text"
                  value={form.cron_expression}
                  onChange={(e) => setForm((f) => ({ ...f, cron_expression: e.target.value }))}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-gray-100 font-mono text-sm"
                  placeholder="0 2 * * *"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">Rules</label>
                <div className="max-h-40 overflow-y-auto border border-gray-300 dark:border-gray-600 rounded-lg p-2 space-y-1">
                  {rules.length === 0 ? (
                    <p className="text-sm text-gray-500 py-2">No rules in database. Add rules first.</p>
                  ) : (
                    rules.map((r) => (
                      <label key={r.rule_id} className="flex items-center gap-2 py-1 cursor-pointer">
                        <input
                          type="checkbox"
                          checked={form.rule_ids.includes(r.rule_id)}
                          onChange={() => toggleRule(r.rule_id)}
                          className="rounded border-gray-300 dark:border-gray-600"
                        />
                        <span className="text-sm text-gray-900 dark:text-gray-100 truncate">
                          {r.name || r.rule_id}
                        </span>
                      </label>
                    ))
                  )}
                </div>
              </div>
              <div className="flex items-center gap-2">
                <input
                  type="checkbox"
                  id="enabled"
                  checked={form.enabled}
                  onChange={(e) => setForm((f) => ({ ...f, enabled: e.target.checked }))}
                  className="rounded border-gray-300 dark:border-gray-600"
                />
                <label htmlFor="enabled" className="text-sm text-gray-700 dark:text-gray-300">
                  Enabled
                </label>
              </div>
              <div className="flex justify-end gap-2 pt-2">
                <button
                  type="button"
                  onClick={() => setModalOpen(false)}
                  className="px-4 py-2 rounded-lg border border-gray-300 dark:border-gray-600 hover:bg-gray-50 dark:hover:bg-gray-700"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  disabled={submitting}
                  className="px-4 py-2 rounded-lg bg-psg-teal text-white hover:bg-psg-teal/90 disabled:opacity-50"
                >
                  {submitting ? 'Saving…' : editingId ? 'Update' : 'Create'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}

export default RuleScheduler
